import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IconX, IconGripVertical } from '@tabler/icons-react';
import { T } from './figma';

const MARGIN = 8;

/**
 * Draggable floating settings panel (Figma's effect popovers).
 *
 * Opens to the LEFT of its trigger so it never covers the right panel, then can be
 * dragged anywhere by its header. Deliberately does NOT close on outside click —
 * the point is to keep it open while you work on the canvas and watch the result.
 * Escape or the × closes it.
 *
 * Rendered as a plain `fixed` layer rather than a portal so it also works in browser
 * fullscreen, where portals to document.body are not painted.
 */
export default function FloatingPanel({ open, onClose, title, anchorRef, width = 240, children }) {
    const [pos, setPos] = useState(null);
    const dragRef = useRef(null);
    const panelRef = useRef(null);

    const clamp = useCallback((x, y) => {
        const w = panelRef.current?.offsetWidth || width;
        const h = panelRef.current?.offsetHeight || 200;
        return {
            x: Math.min(Math.max(MARGIN, x), Math.max(MARGIN, window.innerWidth - w - MARGIN)),
            y: Math.min(Math.max(MARGIN, y), Math.max(MARGIN, window.innerHeight - h - MARGIN)),
        };
    }, [width]);

    // Place it beside the trigger each time it opens (before paint, so it never flashes
    // at the wrong spot). Left of the trigger, falling back to the right if there's no room.
    useLayoutEffect(() => {
        if (!open) { setPos(null); return; }
        const r = anchorRef?.current?.getBoundingClientRect();
        if (!r) { setPos(clamp(MARGIN, MARGIN)); return; }
        const left = r.left - width - 12;
        const x = left >= MARGIN ? left : r.right + 12;
        setPos(clamp(x, r.top - 8));
    }, [open, anchorRef, width, clamp]);

    // Escape closes
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') { e.stopPropagation(); onClose?.(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    // Keep it on-screen if the window is resized
    useEffect(() => {
        if (!open) return undefined;
        const onResize = () => setPos((p) => (p ? clamp(p.x, p.y) : p));
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [open, clamp]);

    const onHeaderPointerDown = (e) => {
        if (e.target.closest('[data-no-drag]')) return;
        e.preventDefault();
        const start = { mx: e.clientX, my: e.clientY, x: pos?.x || 0, y: pos?.y || 0 };
        dragRef.current = start;
        const move = (ev) => {
            const d = dragRef.current;
            if (!d) return;
            setPos(clamp(d.x + (ev.clientX - d.mx), d.y + (ev.clientY - d.my)));
        };
        const up = () => {
            dragRef.current = null;
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
    };

    if (!open || !pos) return null;

    return (
        <div
            ref={panelRef}
            className={`fixed z-[250] rounded-[13px] shadow-2xl border ${T.border} ${T.panel}`}
            style={{ left: pos.x, top: pos.y, width }}
            // Don't let clicks inside reach the canvas / deselect handlers
            onPointerDown={(e) => e.stopPropagation()}
        >
            <div
                onPointerDown={onHeaderPointerDown}
                className={`flex items-center gap-1 h-8 pl-2 pr-1 border-b ${T.border} cursor-grab active:cursor-grabbing select-none`}
            >
                <IconGripVertical size={12} className={T.textFaint} />
                <span className={`flex-1 text-[11px] font-semibold truncate ${T.text}`}>{title}</span>
                <button
                    type="button"
                    data-no-drag
                    title="Close"
                    onClick={onClose}
                    className={`h-5 w-5 shrink-0 flex items-center justify-center rounded-[4px] ${T.textSoft} ${T.hoverBg}`}
                >
                    <IconX size={12} />
                </button>
            </div>
            <div className="p-3 flex flex-col gap-2">{children}</div>
        </div>
    );
}
